import { ArrowRight, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { C, SERIF, SANS, ARTICLES } from "../lib/constants";
import { ARTICLE_BODIES } from "../lib/articles";
import { Btn, Eyebrow } from "../components/Primitives";
import { useSEO } from "../lib/useSEO";
import { SEO, articleSEO } from "../lib/seoRoutes";


function ArticleCard({ a, go }) {
  const href = `/journal/${a.slug}`;
  return (
    <a href={href} data-testid={`journal-card-${a.slug}`}
      onClick={(e) => { e.preventDefault(); go(`journal/${a.slug}`); }}
      style={{ cursor: "pointer", textDecoration: "none", color: "inherit", display: "block" }}>
      <div style={{ position: "relative" }}>
        <img src="/img/journal.png" alt={a.title} style={{ width: "100%", height: 190,
          objectFit: "cover", borderRadius: 12, display: "block" }}
          onError={(e) => { e.target.style.display = "none"; }} />
        <span style={{ position: "absolute", bottom: 10, left: 12,
          fontFamily: SANS, fontSize: 10, background: C.white, color: C.dark,
          padding: "3px 10px", borderRadius: 14, fontWeight: 600 }}>{a.read} read</span>
      </div>
      <div style={{ fontFamily: SANS, fontSize: 9, letterSpacing: 2, color: C.coral,
        textTransform: "uppercase", margin: "12px 0 5px" }}>{a.cat}</div>
      <h3 style={{ fontFamily: SERIF, fontSize: 20, color: C.dark, fontWeight: 400,
        margin: "0 0 8px", lineHeight: 1.3 }}>{a.title}</h3>
      <p style={{ fontFamily: SANS, fontSize: 13, color: C.muted, margin: "0 0 10px",
        lineHeight: 1.65 }}>{a.excerpt}</p>
      <div style={{ fontFamily: SANS, fontSize: 11, color: C.dark, fontWeight: 600 }}>By {a.author}</div>
      <div style={{ fontFamily: SANS, fontSize: 11, color: C.mutedLt }}>{a.role} · {a.date}</div>
    </a>
  );
}

export function JournalPage({ go }) {
  useSEO(SEO.journal);
  const [filter, setFilter] = useState("All");
  const cats = ["All", "Hormones", "PCOS", "Fertility", "Mental Health", "Rest", "Nutrition"];
  const shown = filter === "All" ? ARTICLES
    : ARTICLES.filter(a => a.cat.toLowerCase() === filter.toLowerCase());
  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "52px 28px 72px" }}>
      <Eyebrow>The Journal</Eyebrow>
      <h1 style={{ fontFamily: SERIF, fontSize: 48, color: C.dark, fontWeight: 400,
        margin: "0 0 10px" }}>Slow reading for fast lives.</h1>
      <p style={{ fontFamily: SANS, fontSize: 15, color: C.muted, margin: "0 0 30px",
        lineHeight: 1.7, maxWidth: 600 }}>
        Essays, guides, and quiet notes from doctors, therapists, nutritionists,
        and the women who've been there.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 42 }}>
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`journal-filter-${c.toLowerCase().replace(/ /g, "-")}`} style={{ padding: "7px 16px",
            borderRadius: 20, cursor: "pointer",
            border: `1.5px solid ${filter === c ? C.coral : C.border}`,
            background: filter === c ? C.coralBg : C.white, fontFamily: SANS, fontSize: 12,
            fontWeight: filter === c ? 700 : 400,
            color: filter === c ? C.coral : C.dark }}>{c}</button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 30 }}>
        {shown.map(a => <ArticleCard key={a.id} a={a} go={go} />)}
      </div>
    </div>
  );
}

function Block({ type, text }) {
  if (type === "h2") return <h2 style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 400,
    color: C.dark, margin: "32px 0 14px" }}>{text}</h2>;
  if (type === "quote") return (
    <div style={{ background: C.coralBg, borderRadius: 12, padding: "20px 24px",
      margin: "28px 0", borderLeft: `3px solid ${C.coral}` }}>
      <p style={{ margin: 0, fontFamily: SERIF, fontSize: 18, color: C.dark,
        lineHeight: 1.6, fontStyle: "italic" }}>“{text}”</p>
    </div>
  );
  return <p style={{ margin: "0 0 22px" }}>{text}</p>;
}

export function ArticlePage({ go, slug }) {
  const a = ARTICLES.find(x => x.slug === slug) || ARTICLES[2];
  const body = ARTICLE_BODIES[a.slug];
  useSEO(articleSEO(a));
  const related = ARTICLES.filter(x => x.slug !== a.slug).slice(0, 2);
  return (
    <div data-testid="article-page" style={{ maxWidth: 720, margin: "0 auto", padding: "52px 28px 72px" }}>
      <button onClick={() => go("journal")} data-testid="article-back-btn" style={{ background: "none", border: "none",
        cursor: "pointer", fontFamily: SANS, fontSize: 13, color: C.muted,
        display: "flex", alignItems: "center", gap: 6, marginBottom: 28, padding: 0 }}>
        <ArrowLeft size={13} /> Back to The Journal
      </button>
      <div style={{ fontFamily: SANS, fontSize: 10, letterSpacing: 2, color: C.coral,
        textTransform: "uppercase", fontWeight: 700, marginBottom: 10 }}>{a.cat}</div>
      <h1 data-testid="article-title" style={{ fontFamily: SERIF, fontSize: 40, color: C.dark, fontWeight: 400,
        margin: "0 0 18px", lineHeight: 1.2 }}>{a.title}</h1>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 28,
        paddingBottom: 24, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: C.coralBg,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: SERIF, fontSize: 16, color: C.coral, fontWeight: 700 }}>4E</div>
        <div>
          <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark }}>By {a.author}</div>
          <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>
            {a.role} · {a.date} · {a.read} read</div>
        </div>
      </div>
      <img src="/img/journal.png" alt={a.title} style={{ width: "100%", height: 320,
        objectFit: "cover", borderRadius: 14, display: "block" }}
        onError={(e) => { e.target.style.display = "none"; }} />
      <div style={{ fontFamily: SANS, fontSize: 15, color: C.dark, lineHeight: 1.95, marginTop: 32 }}>
        {body.map(([t, text], i) => <Block key={i} type={t} text={text} />)}
      </div>
      <div style={{ marginTop: 40, paddingTop: 28, borderTop: `1px solid ${C.border}` }}>
        <Eyebrow>Keep reading</Eyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, margin: "8px 0 28px" }}>
          {related.map(r => <ArticleCard key={r.id} a={r} go={go} />)}
        </div>
        <Btn variant="soft" size="sm" onClick={() => go("journal")}>
          More from The Journal <ArrowRight size={13} /></Btn>
      </div>
    </div>
  );
}
